import React, { useState } from 'react';
import { Layout, Menu, Input, Button, Space, Dropdown, Drawer } from 'antd';
import {
  SearchOutlined,
  PlusCircleOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  FireOutlined,
  ShoppingCartOutlined,
  MenuOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useNavigate, Link, useLocation, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../features/auth/authSlice';
import { logout as logoutRequest } from '../features/auth/authApi';
import NotificationBell from '../features/notifications/NotificationBell';

const { Header, Content, Footer } = Layout;

function BrandMark({ dark }) {
  return (
    <Link to="/" className={`df-brand${dark ? ' df-brand--dark' : ''}`} aria-label="DealFusion home">
      <span className="df-brand__mark" aria-hidden="true">DF</span>
      <span className="df-brand__wordmark">DealFusion</span>
    </Link>
  );
}

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const navItems = [
    { key: '/', label: 'Best Deals', icon: <FireOutlined /> },
    { key: '/search', label: 'Browse', icon: <ShoppingCartOutlined /> },
  ];

  const userMenuItems = [
    { key: 'profile', label: 'Profile', icon: <UserOutlined /> },
    { key: 'alerts', label: 'Alerts', icon: <BellOutlined /> },
    { key: 'settings', label: 'Notification settings', icon: <SettingOutlined /> },
    { type: 'divider' },
    { key: 'logout', label: 'Sign Out', icon: <LogoutOutlined />, danger: true },
  ];

  const handleLogout = async () => {
    try {
      await logoutRequest();
    } catch (_) {
      // still clear local session
    }
    dispatch(logout());
    navigate('/login');
  };

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') return handleLogout();
    if (key === 'profile') return navigate('/profile');
    if (key === 'alerts') return navigate('/alerts');
    if (key === 'settings') return navigate('/settings/notifications');
    return undefined;
  };

  const runSearch = (value) => {
    const q = (value ?? searchValue).trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
      <Header className="df-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, minWidth: 0 }}>
          <Button
            type="text"
            className="df-header-menu-btn"
            icon={<MenuOutlined style={{ color: '#F4F7FA', fontSize: 18 }} />}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            style={{ display: 'none', width: 44, height: 44 }}
          />
          <BrandMark />
          <Menu
            className="df-header__nav"
            theme="dark"
            mode="horizontal"
            selectedKeys={[location.pathname === '/' ? '/' : location.pathname.startsWith('/search') ? '/search' : location.pathname]}
            items={navItems}
            style={{
              background: 'transparent',
              borderBottom: 'none',
              fontSize: 14,
              fontWeight: 600,
              minWidth: 220,
              lineHeight: '64px',
            }}
            onClick={({ key }) => navigate(key)}
          />
        </div>

        <Space size={12} wrap={false} style={{ marginLeft: 12 }}>
          <Input
            className="df-header__search"
            prefix={<SearchOutlined style={{ color: 'rgba(244,247,250,0.55)' }} />}
            placeholder="Search deals…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: 220, borderRadius: 8, height: 40 }}
            onPressEnter={() => runSearch()}
            aria-label="Search deals"
          />

          {isAuthenticated ? (
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => navigate('/deals/submit')}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                background: '#E85D04',
                borderColor: '#E85D04',
                height: 40,
                minWidth: 44,
              }}
            >
              <span className="df-submit-label">Submit</span>
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={() => navigate('/login')}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                background: '#E85D04',
                borderColor: '#E85D04',
                height: 40,
              }}
            >
              Sign In
            </Button>
          )}

          {isAuthenticated && <NotificationBell />}

          <Dropdown
            menu={{ items: userMenuItems, onClick: handleMenuClick }}
            placement="bottomRight"
            trigger={['click']}
          >
            <button
              type="button"
              aria-label="Account menu"
              style={{
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 44,
              }}
            >
              <div style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#F4F7FA',
                fontWeight: 700,
                fontSize: 13,
                fontFamily: 'var(--df-font-display)',
              }}>
                {user?.displayName ? user.displayName.charAt(0).toUpperCase() : <UserOutlined />}
              </div>
              <span style={{ color: '#E8EEF4', fontSize: 14, fontWeight: 600, display: 'none' }} className="df-account-name">
                {user?.displayName || 'Account'}
              </span>
            </button>
          </Dropdown>
        </Space>
      </Header>

      <Drawer
        title={<BrandMark dark />}
        placement="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        width={280}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={navItems}
          onClick={({ key }) => { navigate(key); setMobileOpen(false); }}
          style={{ border: 'none' }}
        />
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search deals…"
          style={{ marginTop: 16, borderRadius: 8, height: 44 }}
          onPressEnter={(e) => runSearch(e.target.value)}
        />
      </Drawer>

      <Content className="df-main">
        <Outlet />
      </Content>

      <Footer className="df-footer">
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 14, fontSize: 13, fontWeight: 500 }}>
          <Link to="/">About</Link>
          <Link to="/">Terms</Link>
          <Link to="/">Privacy</Link>
          <Link to="/">Contact</Link>
        </div>
        <div style={{ fontSize: 12, color: 'rgba(244,247,250,0.45)', fontFamily: 'var(--df-font-body)' }}>
          &copy; {new Date().getFullYear()} DealFusion. Find sharper prices, faster.
        </div>
      </Footer>

      <style>{`
        @media (max-width: 768px) {
          .df-header-menu-btn { display: inline-flex !important; }
          .df-submit-label { display: none; }
          .df-account-name { display: none !important; }
        }
        @media (min-width: 900px) {
          .df-account-name { display: inline !important; }
        }
      `}</style>
    </Layout>
  );
}
